import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import certificatePublicKeys from '../data/certificatePublicKeys';

const REQUIRED_FIELDS = [
  'cert_id',
  'recipient_name',
  'rollno',
  'email_sha256',
  'event',
  'sessions_total',
  'issuer_name',
  'issuer_title',
  'issue_date',
  'certificate_type',
  'key_id',
];

const decodeBase64Url = (value) => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4);
  const binary = window.atob(padded);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

const pemToArrayBuffer = (pem) => {
  const base64 = pem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s/g, '');
  return decodeBase64Url(base64).buffer;
};

const tokenFromLocation = () => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const queryParams = new URLSearchParams(window.location.search);
  return hashParams.get('token') || queryParams.get('token') || '';
};

const parseToken = (rawToken) => {
  const token = rawToken.trim();
  const parts = token.split('.');
  if (parts.length !== 3 || parts[0] !== 'v1') {
    throw new Error('Invalid certificate.');
  }

  const payloadText = new TextDecoder().decode(decodeBase64Url(parts[1]));
  const payload = JSON.parse(payloadText);
  const missingField = REQUIRED_FIELDS.find((field) => !payload[field]);
  if (missingField) {
    throw new Error('Invalid certificate.');
  }

  return {
    token,
    payload,
    payloadPart: parts[1],
    signature: decodeBase64Url(parts[2]),
  };
};

const verifyCertificateToken = async (rawToken) => {
  const parsed = parseToken(rawToken);
  const keyConfig = certificatePublicKeys[parsed.payload.key_id];
  if (!keyConfig || keyConfig.algorithm !== 'Ed25519') {
    throw new Error('Invalid certificate.');
  }

  if (!window.crypto?.subtle) {
    throw new Error('This browser cannot verify certificates.');
  }

  const publicKey = await window.crypto.subtle.importKey(
    'spki',
    pemToArrayBuffer(keyConfig.publicKeyPem),
    { name: 'Ed25519' },
    false,
    ['verify']
  );

  const isValid = await window.crypto.subtle.verify(
    { name: 'Ed25519' },
    publicKey,
    parsed.signature,
    new TextEncoder().encode(parsed.payloadPart)
  );

  if (!isValid) {
    throw new Error('Invalid certificate.');
  }

  return parsed.payload;
};

const DetailRow = ({ label, value }) => (
  <div className="border-b border-white-1/10 py-4 sm:grid sm:grid-cols-[180px_1fr] sm:gap-6">
    <dt className="text-sm font-medium text-light-gray/70">{label}</dt>
    <dd className="mt-1 break-words text-base font-semibold text-white-1 sm:mt-0">{value}</dd>
  </div>
);

const CertificateVerify = () => {
  const [token, setToken] = useState(tokenFromLocation);
  const [status, setStatus] = useState(token ? 'verifying' : 'idle');
  const [payload, setPayload] = useState(null);
  const [message, setMessage] = useState('');

  const details = useMemo(() => {
    if (!payload) return [];
    return [
      ['Certificate ID', payload.cert_id],
      ['Recipient', payload.recipient_name],
      ['Roll No', payload.rollno],
      ['Event', payload.event],
      ['Sessions', payload.sessions_total],
      ['Issued On', payload.issue_date],
      ['Issuer', payload.issuer_name],
      ['Issuer Title', payload.issuer_title],
      ['Certificate Type', payload.certificate_type],
      ['Key ID', payload.key_id],
    ];
  }, [payload]);

  const verify = async (tokenToVerify = token) => {
    setStatus('verifying');
    setPayload(null);
    setMessage('');

    try {
      const verifiedPayload = await verifyCertificateToken(tokenToVerify);
      setPayload(verifiedPayload);
      setStatus('valid');
    } catch (error) {
      setMessage(error.message || 'Invalid certificate.');
      setStatus('invalid');
    }
  };

  useEffect(() => {
    const currentToken = tokenFromLocation();
    if (!currentToken) return;
    let isMounted = true;

    setToken(currentToken);
    setStatus('verifying');
    setPayload(null);
    setMessage('');

    verifyCertificateToken(currentToken)
      .then((verifiedPayload) => {
        if (!isMounted) return;
        setPayload(verifiedPayload);
        setStatus('valid');
      })
      .catch((error) => {
        if (!isMounted) return;
        setMessage(error.message || 'Invalid certificate.');
        setStatus('invalid');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <main className="min-h-screen bg-bg-dark px-4 py-8 text-white-1 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-8"
        >
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-accent-yellow">
            Certificate Verification
          </p>
          <h1 className="text-3xl font-semibold text-white-1 sm:text-5xl">
            AWS Silicon Valley Workflow
          </h1>
        </motion.header>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
          className="rounded-xl border border-white-1/10 bg-card-dark/80 p-5 shadow-2xl shadow-black/30 sm:p-8"
        >
          <div className="mb-6 flex flex-col gap-4 border-b border-white-1/10 pb-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-light-gray/70">Verification status</p>
              <p
                className={`mt-1 text-2xl font-semibold ${
                  status === 'valid'
                    ? 'text-emerald-300'
                    : status === 'invalid'
                    ? 'text-red-300'
                    : 'text-accent-yellow'
                }`}
              >
                {status === 'valid'
                  ? 'Valid certificate'
                  : status === 'invalid'
                  ? 'Invalid certificate'
                  : status === 'verifying'
                  ? 'Verifying'
                  : 'Token required'}
              </p>
            </div>
            <div className="rounded-lg border border-white-1/10 bg-bg-dark/80 px-4 py-3 text-sm text-light-gray/80">
              rayyanshaheer.com/verify
            </div>
          </div>

          {status === 'valid' && (
            <dl>
              {details.map(([label, value]) => (
                <DetailRow key={label} label={label} value={value} />
              ))}
            </dl>
          )}

          {status === 'invalid' && (
            <div className="rounded-lg border border-red-300/20 bg-red-500/10 p-4 text-sm text-red-100">
              {message || 'Invalid certificate.'}
            </div>
          )}

          {status !== 'valid' && (
            <form
              className="mt-6"
              onSubmit={(event) => {
                event.preventDefault();
                verify();
              }}
            >
              <label className="block text-sm font-medium text-light-gray/80" htmlFor="token">
                Verification token
              </label>
              <textarea
                id="token"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                rows={5}
                className="mt-2 w-full resize-y rounded-lg border border-white-1/10 bg-bg-dark p-3 font-mono text-sm text-white-1 outline-none transition focus:border-accent-yellow"
              />
              <button
                type="submit"
                className="mt-4 rounded-lg bg-accent-yellow px-5 py-3 text-sm font-semibold text-bg-dark transition hover:bg-accent-yellow/90"
              >
                Verify Certificate
              </button>
            </form>
          )}
        </motion.section>
      </div>
    </main>
  );
};

export default CertificateVerify;
